import { UserRole, type Team, type User } from '../types/index.js';

export type TeamWorkspaceOwnerUser = Pick<User, 'id' | 'email' | 'teamId'>;
export type TeamWorkspaceOwnerTeam = Pick<Team, 'id' | 'ownerUserId' | 'contactEmail' | 'email'>;
export type TeamWorkspaceMember = Pick<User, 'id' | 'role'>;

function normalizeEmail(email: string | null | undefined) {
  return email?.trim().toLowerCase() ?? '';
}

function teamOwnerEmails(team: TeamWorkspaceOwnerTeam) {
  return [team.contactEmail, team.email]
    .map(normalizeEmail)
    .filter((email) => email.length > 0);
}

/** Parent-only workspace with no active team owner or coach staff. */
export function isFamilyTeamWorkspace(members: ReadonlyArray<Pick<User, 'role' | 'accountDisabled'>>): boolean {
  return !members.some(
    (member) => !member.accountDisabled
      && (member.role === UserRole.TEAM_OWNER || member.role === UserRole.COACH),
  );
}

export function canAddTeamMemberRole(
  role: UserRole,
  members: ReadonlyArray<Pick<User, 'role'>>,
): boolean {
  if (role !== UserRole.COACH && role !== UserRole.MECHANIC) {
    return true;
  }

  return !isFamilyTeamWorkspace(members);
}

export const FAMILY_TEAM_STAFF_ROLE_ERROR = 'Family teams can only add parent accounts, not coaches or mechanics.';

export const STAFF_ACCOUNT_DISABLED_MESSAGE = 'Staff access is paused on the Family plan. Switch to a club plan to restore access.';

const PAUSABLE_MEMBER_ROLES = new Set<UserRole>([
  UserRole.TEAM_OWNER,
  UserRole.COACH,
  UserRole.MECHANIC,
  UserRole.PARENT,
]);

/** Staff and parent logins can be paused; drivers keep their account and data. */
export function canPauseTeamMemberAccess(role: UserRole): boolean {
  return PAUSABLE_MEMBER_ROLES.has(role);
}

/** Parent roster members on a family team can co-manage drivers and team settings. */
export function isFamilyTeamParentManager(
  user: Pick<User, 'id' | 'role' | 'teamId'>,
  team: Pick<Team, 'id'> | null | undefined,
  members: ReadonlyArray<TeamWorkspaceMember>,
): boolean {
  if (user.role !== UserRole.PARENT || !user.teamId || !team || user.teamId !== team.id) {
    return false;
  }

  if (!isFamilyTeamWorkspace(members)) {
    return false;
  }

  return members.some((member) => member.id === user.id && member.role === UserRole.PARENT);
}

export function isTeamWorkspaceOwner(
  user: TeamWorkspaceOwnerUser,
  team: TeamWorkspaceOwnerTeam | null | undefined,
): boolean {
  if (!user.teamId || !team || user.teamId !== team.id) {
    return false;
  }

  if (team.ownerUserId) {
    return team.ownerUserId === user.id;
  }

  const userEmail = normalizeEmail(user.email);
  return teamOwnerEmails(team).some((email) => email === userEmail);
}

export function resolveTeamWorkspaceOwnerAccess(
  user: Pick<User, 'id' | 'email' | 'role' | 'teamId'>,
  team: TeamWorkspaceOwnerTeam | null | undefined,
  members: ReadonlyArray<TeamWorkspaceMember>,
): boolean {
  if (isTeamWorkspaceOwner(user, team)) {
    return true;
  }

  return isFamilyTeamParentManager(user, team, members);
}

export function canManageDriversForWorkspace(
  role: UserRole,
  options?: { isTeamWorkspaceOwner?: boolean },
): boolean {
  return (
    role === UserRole.TEAM_OWNER
    || role === UserRole.COACH
    || (role === UserRole.PARENT && Boolean(options?.isTeamWorkspaceOwner))
    || (role === UserRole.DRIVER && Boolean(options?.isTeamWorkspaceOwner))
  );
}

export function canManageTeamForWorkspace(
  role: UserRole,
  options?: { isTeamWorkspaceOwner?: boolean },
): boolean {
  return (
    role === UserRole.TEAM_OWNER
    || role === UserRole.COACH
    || (role === UserRole.PARENT && Boolean(options?.isTeamWorkspaceOwner))
    || (role === UserRole.DRIVER && Boolean(options?.isTeamWorkspaceOwner))
  );
}

export function canViewTeamMembersForWorkspace(
  role: UserRole,
  options?: { isTeamWorkspaceOwner?: boolean },
): boolean {
  return (
    role === UserRole.TEAM_OWNER
    || role === UserRole.COACH
    || role === UserRole.MECHANIC
    || (role === UserRole.PARENT && Boolean(options?.isTeamWorkspaceOwner))
    || (role === UserRole.DRIVER && Boolean(options?.isTeamWorkspaceOwner))
  );
}

export function canManageKartsForWorkspace(
  role: UserRole,
  options?: { isTeamWorkspaceOwner?: boolean },
): boolean {
  return (
    role === UserRole.TEAM_OWNER
    || role === UserRole.COACH
    || role === UserRole.MECHANIC
    || (role === UserRole.PARENT && Boolean(options?.isTeamWorkspaceOwner))
    || (role === UserRole.DRIVER && Boolean(options?.isTeamWorkspaceOwner))
  );
}
